import React, { useEffect, useMemo, useState } from "react";
import { DataGridPro } from '@mui/x-data-grid-pro';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import Alert from '@mui/material/Alert';
import CircularProgress from '@mui/material/CircularProgress';
import Snackbar from '@mui/material/Snackbar';
import { DatePicker } from '@mui/x-date-pickers-pro';
import dayjs from 'dayjs';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import IconButton from '@mui/material/IconButton';

import { getDcoResults } from '../firebase';
import { formatDateTime } from '../utils';

function buildColumns() {
  return [
    { field: 'jobNumber', headerName: 'Job #', flex: 1, renderCell: (params) => (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
        <Typography variant="body2" sx={{ color: 'inherit' }}>
          {params.value}
        </Typography>
        {params.row.jobLink && (
          <IconButton
            size="small"
            href={params.row.jobLink}
            target="_blank"
            rel="noreferrer"
            aria-label="Open in ServiceTitan"
          >
            <OpenInNewIcon fontSize="inherit" />
          </IconButton>
        )}
      </Box>
    ) },
    { field: 'pass_fail', headerName: 'Pass', flex: 1, minWidth: 110, type: 'boolean' },
    { field: 'technicians', headerName: 'Techs', flex: 2, minWidth: 140 },
    { field: 'summary_included', headerName: 'Summary', flex: 1, minWidth: 110, type: 'boolean' },
    { field: 'payment_collected', headerName: 'Payment', flex: 1, minWidth: 110, type: 'boolean' },
    { field: 'matching_dates', headerName: 'Dates', flex: 1, minWidth: 110, type: 'boolean' },
    { field: 'tasks_present', headerName: 'Tasks', flex: 1, minWidth: 110, type: 'boolean' },
    { field: 'pictures_present', headerName: 'Pictures', flex: 1, minWidth: 110, type: 'boolean' },
    { field: '_updated', headerName: 'Updated', flex: 1.5, minWidth: 170, valueFormatter: (value) => formatDateTime(value) },
  ];
}

export default function DcoTable({ accountDetails }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'info' });
  const [since, setSince] = useState(dayjs().subtract(7, 'day'));

  const columns = useMemo(() => buildColumns(), []);

  const showSnackbar = (message, severity = 'info') => setSnackbar({ open: true, message, severity });

  const handleSnackbarClose = (_e, reason) => {
    if (reason === 'clickaway') return;
    setSnackbar(s => ({ ...s, open: false }));
  };

  const handleExport = () => {
    if (!rows.length) return;
    const headers = columns.map(col => col.headerName).join(',');
    const data = rows.map(row => 
      columns.map(col => {
        let value = row[col.field];
        if (col.valueFormatter) value = col.valueFormatter(value);
        if (typeof value === 'boolean') value = value ? 'Yes' : 'No';
        if (value == null) value = '';
        return `"${String(value).replace(/"/g, '""')}"`;
      }).join(',')
    ).join('\n');
    const csv = `${headers}\n${data}`;
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `dco-results-${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const fetchData = async () => {
    setLoading(true);
    setError('');
    try {
      const results = await getDcoResults({
        account: accountDetails?.accounts?.[0],
        since: since ? since.toDate() : undefined,
        limit: 10000,
      });
      setRows(results);
    } catch (e) {
      setError(e.message || 'Failed to load DCO results');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [since]);


  return (
    <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden', pb: 2 }}>
      <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', justifyContent: 'space-between', gap: 2, pt: 1, mb: 2 }}>
        <Typography variant="h5" sx={{ fontWeight: 600 }}>DCO</Typography>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 2 }}>
          <DatePicker
            label="Since"
            value={since}
            onChange={(v) => setSince(v)}
            slotProps={{ textField: { size: 'small' } }}
          />
          <Button variant="outlined" onClick={handleExport}>Export</Button>
          <Button variant="outlined" onClick={fetchData}>Refresh</Button>
        </Box>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}><CircularProgress /></Box>
      ) : (
        <DataGridPro
          rows={rows}
          columns={columns}
          getRowId={(row) => `${row.jobNumber}-${row._updated || row._created || Math.random()}`}
          pagination
          pageSizeOptions={[25, 50, 100]}
          initialState={{
            sorting: { sortModel: [{ field: '_updated', sort: 'desc' }] },
            pagination: { paginationModel: { pageSize: 100 } },
          }}
          density="compact"
          disableRowSelectionOnClick
          headerFilters
        />
      )}
      <Snackbar open={snackbar.open} autoHideDuration={4000} onClose={handleSnackbarClose}>
        <Alert severity={snackbar.severity} onClose={handleSnackbarClose}>{snackbar.message}</Alert>
      </Snackbar>
    </Box>
  );
}
